import { useMutation, useQuery } from '@tanstack/react-query';

import { type CardProviderOption, type FuelCardMatching, FuelCardRequester } from '@cobliteam/api-lib';

import { Environment } from '@cobliteam/react-environments';

const env = Environment.getInstance().getEnvironment();
const fuelCardRequester = new FuelCardRequester({ host: env.herbieApiHost });

const FUELCARD_ACTIVE_QUERY_KEY = 'fuelCardActive';
const FUELCARD_INACTIVE_QUERY_KEY = 'fuelCardInactive';

const useFuelCard = () => {
  const getActiveFuelCardMatchings = () => {
    return useQuery<FuelCardMatching[]>({
      queryKey: [`${FUELCARD_ACTIVE_QUERY_KEY}`],
      queryFn: () => fuelCardRequester.loadFuelCardMatchings('active'),
      retry: false,
      enabled: true,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });
  };

  const getInactiveFuelCardMatchings = () => {
    return useQuery<FuelCardMatching[]>({
      queryKey: [`${FUELCARD_INACTIVE_QUERY_KEY}`],
      queryFn: () => fuelCardRequester.loadFuelCardMatchings('inactive'),
      retry: false,
      enabled: true,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });
  };

  const deleteFuelCardMatchings = useMutation<void, unknown, { fuelCardIds: string[] }>({
    mutationFn: (fuelCardDeleteContent) => fuelCardRequester.deactivateFuelCards(fuelCardDeleteContent.fuelCardIds),
  });

  const recoverFuelCardMatchings = useMutation<void, unknown, { fuelCardIds: string[] }>({
    mutationFn: (fuelCardRecoverContent) => fuelCardRequester.activateFuelCards(fuelCardRecoverContent.fuelCardIds),
  });

  const deleteTransactions = useMutation<void, unknown, { transactionIds: string[] }>({
    mutationFn: (data) => fuelCardRequester.deleteTransactions(data.transactionIds),
  });

  const requestIntegration = useMutation<void, unknown, { provider: CardProviderOption; cnpj?: string }>({
    mutationFn: (data) => fuelCardRequester.requestIntegration(data.provider, data.cnpj),
  });

  return {
    getActiveFuelCardMatchings,
    getInactiveFuelCardMatchings,
    deleteFuelCardMatchings,
    recoverFuelCardMatchings,
    deleteTransactions,
    requestIntegration,
  };
};

export default useFuelCard;
